import * as React from "react"
import type { ReactNode } from "react"
import { Info } from "lucide-react"

import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

interface PageHeaderProps {
  title: string
  /** Contexto de la pantalla en versalitas: modulo, entidad, periodo. */
  eyebrow?: ReactNode
  /**
   * Que es esta pantalla. PLEGADA por defecto, detras del ⓘ junto al titulo:
   * ver «La explicacion se pide, no se impone».
   */
  description?: ReactNode
  actions?: ReactNode
  className?: string
}

/**
 * Cabecera de pantalla. Una sola linea de titulo, la explicacion en gris y
 * medida (65ch): en una herramienta el texto explicativo se lee una vez y
 * despues estorba, asi que no puede ocupar el ancho completo de la tabla.
 *
 * EL TITULO ES UN TITULAR, en el sentido que le da el brandbook (lamina 18):
 * SemiBold, interletrado -3% y punto final rojo — «punto de entrada de cada
 * pieza». El punto lo pone la clase `.punto` como pseudoelemento, asi que el
 * texto que se pasa NO lo lleva: escribirlo a mano lo dejaria en negro y
 * duplicado.
 *
 * ---------------------------------------------------------------------------
 * LA EXPLICACION SE PIDE, NO SE IMPONE
 *
 * Cesar, 2026-10-04: «reduciendo texto para que se vea mas limpio». La frase
 * de «se lee una vez y despues estorba» ya estaba escrita arriba, y la
 * pantalla seguia mostrandola siempre. Ahora la cabecera es el titulo y un ⓘ;
 * la explicacion aparece al pulsarlo y se cierra con el mismo boton. El texto
 * sigue existiendo en cada pantalla —es lo que dice que ES la pantalla— y la
 * guia de la plataforma lo sigue encontrando por `data-guia="cabecera"`.
 */
export function PageHeader({
  title,
  eyebrow,
  description,
  actions,
  className,
}: PageHeaderProps) {
  const [abierta, setAbierta] = React.useState(false)

  return (
    <header data-guia="cabecera" className={cn("flex flex-wrap items-start justify-between gap-x-6 gap-y-2", className)}>
      <div className="min-w-0">
        {eyebrow ? <div className="eyebrow mb-0.5">{eyebrow}</div> : null}
        <div className="flex items-center gap-1.5">
          <h1 className="titular punto text-lg leading-tight text-foreground">{title}</h1>
          {description ? (
            <button
              type="button"
              onClick={() => setAbierta((v) => !v)}
              aria-expanded={abierta}
              aria-label={t("Qué es esta pantalla")}
              title={t("Qué es esta pantalla")}
              className={cn(
                "ml-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground",
                "transition-colors hover:bg-accent hover:text-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                abierta && "bg-accent text-foreground",
              )}
            >
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          ) : null}
        </div>
        {description && abierta ? (
          <p className="mt-1 max-w-[68ch] text-xs leading-[1.2rem] text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">{actions}</div>
      ) : null}
    </header>
  )
}
